import { useCallback, useEffect, useRef, useState } from 'react';
import { IconCrop, IconRotate, IconRotateClockwise, IconX } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { NID_CARD_ASPECT_RATIO, NID_CARD_INCH } from '../../image/nid-to-pdf/nidCardConstants';
import NidQuadCropPanel from './NidQuadCropPanel';
import { useBodyScrollLock, useFitBox } from './useFullscreenStage';

/**
 * Full screen, and the photo never scrolls: every corner handle has to be in
 * view to set the four corners. The stage is measured and the panel fits the
 * picture to it (see useFullscreenStage).
 */
export default function NidCardCropModal({
    opened,
    onClose,
    imageSrc,
    initialQuad,
    title,
    fileName,
    onApply,
    busy = false,
}) {
    const { t } = useTranslation();
    const quadRef = useRef(null);
    const stageRef = useRef(null);
    const [ready, setReady] = useState(false);
    // Quarter turns, clockwise. A photo of a card is often taken sideways, and with
    // the card on its side nobody can tell which dot is the card's top — so the
    // picture can be turned here until the card reads upright.
    const [turns, setTurns] = useState(0);
    const [turnedSrc, setTurnedSrc] = useState(null);
    const fitBox = useFitBox(stageRef, opened);
    useBodyScrollLock(opened);

    useEffect(() => {
        if (!opened) setTurns(0);
    }, [opened, imageSrc]);

    useEffect(() => {
        if (!opened || !imageSrc || turns === 0) {
            setTurnedSrc(null);
            return undefined;
        }
        let alive = true;
        let url = null;
        const img = new Image();
        img.onload = () => {
            const odd = turns % 2 === 1;
            const canvas = document.createElement('canvas');
            canvas.width = odd ? img.naturalHeight : img.naturalWidth;
            canvas.height = odd ? img.naturalWidth : img.naturalHeight;
            const ctx = canvas.getContext('2d');
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate((turns * Math.PI) / 2);
            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
            canvas.toBlob((blob) => {
                if (!alive || !blob) return;
                url = URL.createObjectURL(blob);
                setTurnedSrc(url);
            }, 'image/jpeg', 0.95);
        };
        img.src = imageSrc;
        return () => {
            alive = false;
            if (url) URL.revokeObjectURL(url);
        };
    }, [opened, imageSrc, turns]);

    const rotate = useCallback((delta) => {
        setReady(false);
        setTurnedSrc(null);
        setTurns((v) => (v + delta + 4) % 4);
    }, []);

    // What the panel draws: the original, or the turned copy once it is ready.
    const stageSrc = turns === 0 ? imageSrc : turnedSrc;

    useEffect(() => {
        if (!opened) {
            setReady(false);
        }
    }, [opened, imageSrc]);

    useEffect(() => {
        if (!opened) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape' && !busy) onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [opened, onClose, busy]);

    const handleApply = useCallback(() => {
        const q = quadRef.current;
        if (!q) return;
        void (async () => {
            try {
                const blob = await q.getWarpedJpegBlob();
                if (!blob) return;
                // The quad goes up with the blob so reopening this slot can
                // start from the frame that produced the crop.
                // Corners drawn on a turned copy mean nothing on the original, so a
                // turned crop reopens from a fresh detection instead.
                await Promise.resolve(onApply(blob, turns === 0 ? q.getQuad() : null)).catch(() => {});
            } catch (e) {
                const message = e?.message === 'invalid quad'
                    ? t('nidToPdf.crop.invalidQuad')
                    : t('nidToPdf.crop.failed');
                notifications.show({
                    title: t('nidToPdf.crop.title'),
                    message,
                    color: 'red',
                });
            }
        })();
    }, [onApply, t, turns]);

    if (!opened) return null;

    return (
        <div
            className="fixed inset-0 z-[200] flex h-dvh w-screen flex-col overflow-hidden bg-white dark:bg-zinc-900"
            role="dialog"
            aria-modal="true"
        >
            <div className="flex flex-none items-center justify-between gap-3 border-b border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                <div className="flex min-w-0 items-center gap-2">
                    <IconCrop size={18} className="shrink-0 text-amber-600 dark:text-amber-400" />
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-[#1C1815] dark:text-zinc-100">
                            {t('nidToPdf.crop.titleWithSide', { side: title })}
                        </p>
                        {fileName ? (
                            <p className="truncate text-xs text-[#A8A099] dark:text-zinc-500">{fileName}</p>
                        ) : null}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    disabled={busy}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#ECE8E4] text-[#6B635C] transition-colors hover:border-primary/40 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-300"
                    aria-label={t('nidToPdf.crop.cancel')}
                >
                    <IconX size={16} stroke={2} />
                </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#1C1815] p-3 sm:p-5">
                <div className="mb-3 flex flex-none items-start justify-between gap-3">
                    <p className="text-[13px] leading-relaxed text-white/75">
                        {t('nidToPdf.crop.hint', {
                            w: toBnDigits(NID_CARD_INCH.w),
                            h: toBnDigits(NID_CARD_INCH.h),
                            ratio: toBnDigits(NID_CARD_ASPECT_RATIO.toFixed(2)),
                        })}
                    </p>
                    <div className="flex flex-none items-center gap-2">
                        {[
                            { id: 'left', icon: IconRotate, delta: -1, label: t('nidToPdf.crop.rotateLeft') },
                            { id: 'right', icon: IconRotateClockwise, delta: 1, label: t('nidToPdf.crop.rotateRight') },
                        ].map(({ id, icon: Icon, delta, label }) => (
                            <span key={id} className="group relative">
                                <button
                                    type="button"
                                    onClick={() => rotate(delta)}
                                    disabled={busy || !imageSrc}
                                    aria-label={label}
                                    className="flex h-12 w-12 items-center justify-center rounded-xl bg-white text-[#1C1815] shadow-md transition-colors hover:bg-amber-100 disabled:opacity-40"
                                >
                                    <Icon size={28} stroke={2} />
                                </button>
                                <span className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 whitespace-nowrap rounded-md bg-black px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                                    {label}
                                </span>
                            </span>
                        ))}
                    </div>
                </div>
                <div ref={stageRef} className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden">
                    {stageSrc ? (
                        <NidQuadCropPanel
                            key={turns}
                            ref={quadRef}
                            imageSrc={stageSrc}
                            initialQuad={turns === 0 ? initialQuad : null}
                            fitBox={fitBox}
                            onLoaded={() => setReady(true)}
                        />
                    ) : (
                        <div className="text-sm text-white/70">
                            {t('nidToPdf.crop.loading')}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-none items-center justify-end gap-2 border-t border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={busy}
                    className="rounded-xl border border-[#ECE8E4] bg-white px-4 py-2 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                >
                    {t('nidToPdf.crop.cancel')}
                </button>
                <button
                    type="button"
                    onClick={handleApply}
                    disabled={!ready || !stageSrc || busy}
                    className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {busy ? t('nidToPdf.crop.applying') : t('nidToPdf.crop.apply')}
                </button>
            </div>
        </div>
    );
}
